import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Box, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Modal, Radio, RadioGroup, Switch, TextField,
} from "@mui/material";
import Button from "../../../Subcomponents/Button";
import Close from "../../Icons/Close";
import { errorTost, successTost } from "../../../Services/Toast";
import {
  getSettingsMeta, getCompanySettings, saveCompanySettings, getProjectSettings, saveProjectSettings,
  getOffices, addOffice, saveOffice, getRiders, addRider, saveRider, getTeam, saveTeam, uploadSignatoryImage, getSignatoryImageLink, settingsError,
} from "../../../Services/Crm2Settings";
import cssClasses from "./Crm2Settings.module.css";
import Crm2ConsentTerms from "../Consent/Crm2ConsentTerms";
import Crm2ConsentAskNow from "../Consent/Crm2ConsentAskNow";
import Crm2ConsentReport from "../Consent/Crm2ConsentReport";
import Crm2ConsentDocuments from "../Consent/Crm2ConsentDocuments";
import { NotificationEvents } from "./NotificationsSection";

// CRM 2.0 settings screens (M19). Every section is drawn from the field list the
// server sends (GET /crm2/settings/meta, services/crm2Settings.js on the backend),
// so labels, P / C and checks live in one place.
//   <Crm2CompanySettings />             Settings › CRM 2.0 (company defaults)
//   <Crm2ProjectSettings projectId />   Project › CRM 2.0 (one project's own values)

const OFFICES = "OFFICES";
const RIDERS = "RIDERS";
const TEAM = "TEAM";
// Authorised signatories (DC-231): their own permission, not Settings › CRM 2.0
const SIGNATORIES = "AUTHORISED_SIGNATORIES";
const SIG_VIEW = ["CRM 2.0", "Settings › Authorised signatories", "View"];
const SIG_EDIT = ["CRM 2.0", "Settings › Authorised signatories", "Edit"];
// Notifications (M1-F8): their own permission, like Authorised signatories. Edit changes texts,
// people, channels and template; Go live switches a message On / Off (the server checks both).
const NOTIFICATIONS = "NOTIFICATIONS";
const NOTE_VIEW = ["CRM 2.0", "Settings › Notifications", "View"];
const NOTE_EDIT = ["CRM 2.0", "Settings › Notifications", "Edit"];
const NOTE_GO_LIVE = ["CRM 2.0", "Settings › Notifications", "Go live (switch a message Live or Off)"];
const COMPANY_NAV = ["DAYS_RULES", "APPOINTMENT", OFFICES, RIDERS, "HOLIDAY_LISTS", "DISCOUNT", "CF_LENDERS", SIGNATORIES, "DESK_ALERTS", NOTIFICATIONS, "CANCELLATION", "REASONS", "CUSTOMER_CONSENT", "DOCUMENTS", "TEMPLATE_BUILDER", "DASHBOARD"];
const PROJECT_NAV = ["DAYS_RULES", TEAM, "ASSIGNMENT_RULES", "DISCOUNT", "APPOINTMENT", "CF_NOC_SETUP", "SIGNATORY"];
// Customer Consent tabs (M1-F6, prototype A21t): Consent points is this section's form;
// the other tabs are their own screens (Components/Crm2/Consent).
const CONSENT_TABS = ["Documents", "Terms", "Consent points", "Ask now", "Consent report"];
const SPECIAL = {
  [OFFICES]: { label: "Sub-Registrar offices", sub: "Offices are shared by all projects; each project picks its offices in Project › CRM 2.0 › Appointment booking." },
  [RIDERS]: { label: "Rider pool", sub: "Riders take the original agreement to the Sub-Registrar office and mark the registration done. A Rider signs in with this mobile and an OTP; no Sales App login is needed. How a Rider is suggested, the Not Done cut-off and the sign-in days are in Appointments › Rider choice." },
  [TEAM]: { label: "Team", sub: "Tick the CRMs who get new CRM 2.0 bookings of this project. Only CRMs with access to the project are listed; unticking keeps their existing bookings." },
};

const inputStyle = { fontSize: "1.4rem" };
const menuSx = { fontSize: "1.4rem" };
const btnPrimary = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function useCanEdit(path) {
  const user = useSelector((state) => state.Auth.user);
  let node = user?.roleId?.accessibleTabs;
  for (const key of path) node = node?.[key];
  return user?.roleId?.level == 1 || !!node?.isAccessible;
}

// Which fields a screen shows and saves.
function fieldsFor(def, mode) {
  return Object.entries(def.fields).filter(([, r]) => {
    if (r.type === "hidden") return false;
    if (mode === "company") return !r.projectOnly;
    if (def.rows === "PROJECT") return true;
    return r.scope === "P";
  });
}
function editableKeys(def, mode) {
  return fieldsFor(def, mode).filter(([, r]) => !r.readOnly).map(([k]) => k);
}
function pickKeys(values, keys) {
  const out = {};
  keys.forEach((k) => {
    if (values && values[k] !== undefined) out[k] = values[k];
  });
  return out;
}
function emptyValue(rule, key, rowCount = 0) {
  switch (rule.type) {
    case "bool": return key === "isActive";
    case "int":
    case "num": return rule.nullable ? null : key === "sortOrder" ? rowCount + 1 : rule.min;
    case "enum": return rule.options[0]?.value ?? null;
    case "multi":
    case "numList":
    case "textList":
    case "refMulti":
    case "list": return [];
    case "text": return rule.nullable ? null : "";
    default: return null;
  }
}
function newId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const b = window.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
function newRow(item, rowCount) {
  const row = {};
  Object.entries(item).forEach(([k, r]) => {
    if (r.type === "hidden" && r.clientId) row[k] = newId();
    else if (r.type !== "hidden" && !r.readOnly) row[k] = emptyValue(r, k, rowCount);
  });
  return row;
}
function errorText(err) {
  if (!err) return null;
  if (typeof err === "string") return err;
  return err.message || "Please fix this";
}

// ---- small inputs -------------------------------------------------------------------
function ChipList({ values, onChange, disabled, numeric, prefix = "", suffix = "" }) {
  const [text, setText] = useState("");
  const add = () => {
    const raw = text.trim();
    setText("");
    if (!raw) return;
    const v = numeric ? Number(raw) : raw;
    if (numeric && !Number.isFinite(v)) return;
    if (values.includes(v)) return;
    const next = [...values, v];
    onChange(numeric ? next.sort((a, b) => a - b) : next);
  };
  return (
    <div className={`${cssClasses.chipInput} ${disabled ? cssClasses.chipInputDisabled : ""}`}>
      {values.map((v) => (
        <Chip key={String(v)} size="small" label={`${prefix ? prefix + " " : ""}${v}${suffix}`} sx={{ fontSize: "1.2rem" }}
          onDelete={disabled ? undefined : () => onChange(values.filter((x) => x !== v))} />
      ))}
      {!disabled && (
        <input placeholder="Add" aria-label="Add" value={text} inputMode={numeric ? "decimal" : "text"}
          onChange={(e) => setText(numeric ? e.target.value.replace(/[^\d.]/g, "") : e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={() => text && add()} />
      )}
    </div>
  );
}

// ---- images (Authorised signatories) -------------------------------------------------------
// A private image shown through a 60-second link; nothing public is stored or shown.
function ImagePreview({ path, alt, height = "6.4rem" }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setUrl(null);
    setFailed(false);
    if (path) getSignatoryImageLink(path).then((d) => alive && setUrl(d.url)).catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [path]);
  if (!path) return null;
  if (failed) return <span className={cssClasses.hint}>The image could not be shown</span>;
  if (!url) return <span className={cssClasses.hint}>Loading image…</span>;
  return <img src={url} alt={alt} style={{ height, maxWidth: "24rem", objectFit: "contain", border: "1px solid #EAECF0", borderRadius: ".6rem", background: "#FFFFFF", padding: ".4rem" }} />;
}

// Upload or replace one image (PNG / JPG; the server checks type and size). A new upload is a
// new file: the old one stays in the bucket, so the history shows which image was in use.
function ImageInput({ rule, value, onChange, disabled, folderId }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!folderId) {
      errorTost("Reload the page and try again");
      return;
    }
    setBusy(true);
    try {
      const { path } = await uploadSignatoryImage(file, folderId);
      onChange(path);
      successTost("Image uploaded. Save to keep it.");
    } catch (err) {
      errorTost(settingsError(err).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", flexWrap: "wrap" }}>
      <ImagePreview path={value} alt={rule.label} />
      {!disabled && (
        <>
          <input ref={input} type="file" accept="image/png,image/jpeg" hidden onChange={pick} aria-label={rule.label} />
          <button type="button" className={cssClasses.pillEdit} disabled={busy} onClick={() => input.current?.click()}>
            {busy ? "Uploading…" : value ? "Replace" : "Upload"}
          </button>
          {value && !rule.required && (
            <button type="button" className={cssClasses.pillEdit} disabled={busy} onClick={() => onChange(null)}>Remove</button>
          )}
        </>
      )}
    </div>
  );
}

function FieldInput({ rule, value, onChange, disabled, options, error, compact, row }) {
  const ro = disabled || rule.readOnly;
  const width = compact ? "100%" : undefined;
  switch (rule.type) {
    case "bool":
      return <Switch checked={!!value} disabled={ro} onChange={(e) => onChange(e.target.checked)} inputProps={{ "aria-label": rule.label }} />;
    case "int":
    case "num":
      return (
        <TextField size="small" type="number" value={value ?? ""} disabled={ro} error={!!error}
          onChange={(e) => onChange(e.target.value === "" ? (rule.nullable ? null : "") : Number(e.target.value))}
          inputProps={{ style: inputStyle, step: rule.type === "num" ? "any" : 1, "aria-label": rule.label }}
          sx={{ width: width || "16rem" }} />
      );
    case "text":
      return (
        <TextField size="small" value={value ?? ""} disabled={ro} error={!!error} multiline={!!rule.multiline} minRows={rule.multiline ? 2 : undefined}
          onChange={(e) => onChange(e.target.value)} inputProps={{ style: inputStyle, maxLength: rule.maxLength, "aria-label": rule.label }}
          sx={{ width: width || (rule.multiline ? "100%" : "36rem"), maxWidth: "100%" }} />
      );
    case "time":
      return (
        <TextField size="small" type="time" value={value ?? ""} disabled={ro} error={!!error} onChange={(e) => onChange(e.target.value)}
          inputProps={{ style: inputStyle, "aria-label": rule.label }} sx={{ width: width || "14rem" }} />
      );
    case "date":
      return (
        <TextField size="small" type="date" value={value ? dayjs(Number(value)).format("YYYY-MM-DD") : ""} disabled={ro} error={!!error}
          onChange={(e) => onChange(e.target.value ? dayjs(e.target.value).valueOf() : null)}
          inputProps={{ style: inputStyle, "aria-label": rule.label }} sx={{ width: width || "18rem" }} />
      );
    case "enum":
      if (rule.display === "radio" && !compact) {
        return (
          <RadioGroup value={value === null ? "__null" : String(value)}
            onChange={(e) => onChange(rule.options.find((o) => String(o.value) === e.target.value || (o.value === null && e.target.value === "__null"))?.value ?? null)}>
            {rule.options.map((o) => (
              <FormControlLabel key={String(o.value)} value={o.value === null ? "__null" : String(o.value)} disabled={ro}
                control={<Radio size="small" />} label={<span className={cssClasses.radioText}>{o.label}</span>} />
            ))}
          </RadioGroup>
        );
      }
      return (
        <TextField select size="small" value={value === null || value === undefined ? "__null" : String(value)} disabled={ro} error={!!error}
          onChange={(e) => onChange(rule.options.find((o) => (o.value === null ? "__null" : String(o.value)) === e.target.value)?.value ?? null)}
          SelectProps={{ style: inputStyle }} inputProps={{ "aria-label": rule.label }} sx={{ width: width || "32rem", maxWidth: "100%" }}>
          {rule.options.map((o) => (
            <MenuItem key={String(o.value)} value={o.value === null ? "__null" : String(o.value)} sx={menuSx}>{o.label}</MenuItem>
          ))}
        </TextField>
      );
    case "ref": {
      const list = options[rule.source] || [];
      const shown = list.filter((o) => o.isActive !== false || o.value === value);
      return (
        <TextField select size="small" value={value ?? "__null"} disabled={ro} error={!!error}
          onChange={(e) => onChange(e.target.value === "__null" ? null : e.target.value)}
          SelectProps={{ style: inputStyle }} inputProps={{ "aria-label": rule.label }} sx={{ width: width || "32rem", maxWidth: "100%" }}>
          <MenuItem value="__null" sx={menuSx}>{rule.emptyLabel || "—"}</MenuItem>
          {shown.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={menuSx}>{o.label}{o.isActive === false ? " (off)" : ""}</MenuItem>
          ))}
        </TextField>
      );
    }
    case "multi":
    case "refMulti": {
      const list = rule.type === "multi" ? rule.options : (options[rule.source] || []).filter((o) => o.isActive !== false || (value || []).includes(o.value));
      const current = value || [];
      if (!list.length) return <p className={cssClasses.hint}>Nothing to choose from yet.</p>;
      if (rule.searchable) return <SearchableMulti list={list} current={current} onChange={onChange} disabled={ro} />;
      return (
        <div className={cssClasses.checkWrap}>
          {list.map((o) => (
            <FormControlLabel key={String(o.value)} disabled={ro}
              control={<Checkbox size="small" checked={current.includes(o.value)}
                onChange={(e) => onChange(e.target.checked ? [...current, o.value] : current.filter((v) => v !== o.value))} />}
              label={<span className={cssClasses.radioText}>{o.label}</span>} />
          ))}
        </div>
      );
    }
    case "numList":
      return <ChipList values={value || []} onChange={onChange} disabled={ro} numeric prefix={rule.chipPrefix} suffix={rule.chipSuffix} />;
    case "textList":
      return <ChipList values={value || []} onChange={onChange} disabled={ro} />;
    case "json":
      return <pre className={cssClasses.jsonBox}>{JSON.stringify(value ?? null, null, 1)}</pre>;
    case "list":
      return <ListEditor rule={rule} rows={value || []} onChange={onChange} disabled={ro} options={options} error={error} />;
    case "image":
      // the row's id is the folder its images upload into
      return <ImageInput rule={rule} value={value} onChange={onChange} disabled={ro} folderId={row?.[rule.folderKey || "signatoryId"]} />;
    default:
      return null;
  }
}

function Field({ rule, value, onChange, disabled, options, error, row }) {
  const inline = rule.type === "bool";
  const label = (
    <span className={cssClasses.label}>
      {rule.label}
      {rule.required && <span className={cssClasses.required}> *</span>}
    </span>
  );
  return (
    <div className={inline ? cssClasses.switchLine : cssClasses.field} style={rule.type === "list" || rule.multiline ? { width: "100%" } : undefined}>
      <div className={cssClasses.field}>
        {label}
        {!inline && <FieldInput rule={rule} value={value} onChange={onChange} disabled={disabled} options={options} error={error} row={row} />}
        {rule.hint && <p className={cssClasses.hint}>{rule.hint}</p>}
        {error && <p className={cssClasses.error}>{errorText(error)}</p>}
      </div>
      {inline && <FieldInput rule={rule} value={value} onChange={onChange} disabled={disabled} options={options} error={error} />}
    </div>
  );
}

// ---- lists (one UI pattern: count left, "+ Add" right, Active switch, Edit on the right)
function ConfirmDialog({ open, title, text, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle sx={{ fontSize: "1.6rem", fontFamily: "Inter-Medium" }}>{title}</DialogTitle>
      <DialogContent><p className={cssClasses.headSub}>{text}</p></DialogContent>
      <DialogActions sx={{ padding: "1.6rem" }}>
        <Button style={btnSecondary} btnName="Cancel" clicked={onCancel} />
        <Button style={btnPrimary} btnName="Confirm" clicked={onConfirm} />
      </DialogActions>
    </Dialog>
  );
}

// M2-F1 (DC-19): CRM picker with a search box and Select all / Clear. The order picked
// is the turn order, so ticking adds to the end and the chips show that order.
function SearchableMulti({ list, current, onChange, disabled }) {
  const [q, setQ] = useState("");
  const shown = list.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()));
  const label = (v) => list.find((o) => o.value === v)?.label || "?";
  return (
    <div style={{ width: "100%" }}>
      {current.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".6rem", marginBottom: ".8rem" }}>
          {current.map((v, i) => (
            <span key={v} className={cssClasses.radioText} style={{ background: "#EFF8FF", color: "#175CD3", borderRadius: "1.6rem", padding: ".2rem 1rem" }}>
              {i + 1}. {label(v)}{" "}
              {!disabled && (
                <button type="button" aria-label={`Remove ${label(v)}`} style={{ border: 0, background: "none", color: "inherit", cursor: "pointer" }}
                  onClick={() => onChange(current.filter((x) => x !== v))}>✕</button>
              )}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: ".6rem" }}>
        <input placeholder="Search CRM" aria-label="Search CRM" value={q} disabled={disabled} onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, height: "3.6rem", border: "1px solid #D0D5DD", borderRadius: ".8rem", padding: "0 1rem", fontSize: "1.4rem" }} />
        {!disabled && (
          <button type="button" className={cssClasses.pillEdit}
            onClick={() => onChange([...current, ...shown.map((o) => o.value).filter((v) => !current.includes(v))])}>Select all</button>
        )}
        {!disabled && <button type="button" className={cssClasses.pillEdit} onClick={() => onChange([])}>Clear</button>}
      </div>
      <div className={cssClasses.checkWrap}>
        {shown.map((o) => (
          <FormControlLabel key={String(o.value)} disabled={disabled}
            control={<Checkbox size="small" checked={current.includes(o.value)}
              onChange={(e) => onChange(e.target.checked ? [...current, o.value] : current.filter((v) => v !== o.value))} />}
            label={<span className={cssClasses.radioText}>{o.label}</span>} />
        ))}
        {!shown.length && <p className={cssClasses.hint}>No CRM matches this search.</p>}
      </div>
    </div>
  );
}

// M2-F1 (DC-19): plain-language preview of a CRM assignment rule.
function rulePreview(kind, draft, options) {
  if (kind !== "ASSIGNMENT_RULE") return null;
  const name = (src, v) => (options[src] || []).find((o) => o.value === v)?.label;
  const rs = (n) => "₹" + Number(n).toLocaleString("en-IN");
  const from = draft.unitCostFrom ?? null;
  const to = draft.unitCostTo ?? null;
  const ifs = [
    draft.wingId ? `wing ${name("wings", draft.wingId) || "?"}` : "any wing",
    draft.inventoryTypeId ? `unit type ${name("inventoryTypes", draft.inventoryTypeId) || "?"}` : "any unit type",
    ...(draft.displayUnitType ? [`display unit type ${draft.displayUnitType}`] : []),
    from !== null && to !== null ? `unit cost ${rs(from)} – ${rs(to)}` : from !== null ? `unit cost from ${rs(from)}` : to !== null ? `unit cost up to ${rs(to)}` : "any unit cost",
  ];
  const crms = (draft.crmUserIds || []).map((v) => name("projectCrms", v) || "?");
  return `IF the booking is in ${ifs.join(" · ")}, THEN give it to ${crms.length ? crms.join(" → ") : "(pick at least one CRM)"}${crms.length > 1 ? ", taking turns" : ""}.`;
}

function RowModal({ open, title, item, row, isNew, options, onCancel, onSave, preview }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => setDraft(row), [row]);
  if (!open || !draft) return null;
  return (
    <Modal open={open} onClose={(_, reason) => reason !== "backdropClick" && onCancel()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={cssClasses.modalWrapper}>
        <div className={cssClasses.modalHeader}>
          <span>{title}</span>
          <span className={cssClasses.modalClose} onClick={onCancel} tabIndex={0} role="button" aria-label="Close">
            <Close width="1.2rem" height="1.2rem" />
          </span>
        </div>
        <div className={cssClasses.modalContent}>
          {Object.entries(item).filter(([, r]) => r.type !== "hidden").map(([k, r]) => (
            <Field key={k} rule={r} value={draft[k]} options={options} disabled={r.readOnly} row={draft}
              onChange={(v) => setDraft({ ...draft, [k]: v })} />
          ))}
          {preview && (
            <div style={{ marginTop: "1.6rem", background: "#fff", border: "1px solid #EAECF0", borderRadius: ".8rem", padding: "1.2rem 1.4rem", fontSize: "1.4rem", color: "#344054" }}>
              {rulePreview(preview, draft, options)}
            </div>
          )}
        </div>
        <div className={cssClasses.modalFooter}>
          <Button style={btnSecondary} btnName="Cancel" clicked={onCancel} />
          <Button style={btnPrimary} btnName={isNew ? "Add" : "Save"} clicked={() => onSave(draft)} />
        </div>
      </Box>
    </Modal>
  );
}

function cellText(rule, value, options) {
  if (value === null || value === undefined || value === "") return "—";
  switch (rule.type) {
    case "enum": return rule.options.find((o) => o.value === value)?.label ?? String(value);
    case "ref": return (options[rule.source] || []).find((o) => o.value === value)?.label ?? "—";
    case "refMulti": return value.map((v) => (options[rule.source] || []).find((o) => o.value === v)?.label || "?").join(", ") || "—";
    case "multi": return value.map((v) => rule.options.find((o) => o.value === v)?.label ?? v).join(", ") || "—";
    case "numList":
    case "textList": return value.join(", ") || "—";
    case "list": return `${value.length} ${value.length === 1 ? "row" : "rows"}`;
    case "date": return dayjs(Number(value)).format("DD MMM YYYY");
    case "bool": return value ? "Yes" : "No";
    case "image": return "Uploaded";
    default: return String(value);
  }
}

function ListEditor({ rule, rows, onChange, disabled, options, error }) {
  const [editing, setEditing] = useState(null); // { index, row } (index -1 = new)
  const [confirm, setConfirm] = useState(null);
  const [toDelete, setToDelete] = useState(null); // index waiting for the delete confirm (rule.confirmDelete)
  // up / down (rule.reorder): swap two rows and renumber sortOrder 1, 2, 3 … (rules are checked top-down).
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(rule.item.sortOrder ? next.map((r, k) => ({ ...r, sortOrder: k + 1 })) : next);
  };
  const columns = Object.entries(rule.item).filter(([k, r]) => r.type !== "hidden" && k !== "isActive");
  // rule.offConfirmUsage: options[...] maps a row id to the records that use it ({projects} in offConfirmText)
  const idKey = Object.keys(rule.item).find((k) => rule.item[k].type === "hidden");
  const usedBy = (id) => (rule.offConfirmUsage ? options[rule.offConfirmUsage]?.[id] || [] : []);
  const hasActive = !!rule.item.isActive;
  const rowErrors = (error && typeof error === "object" && error.rows) || {};
  const singular = rule.label.replace(/s$/, "").toLowerCase().replace(/\bcrm\b/g, "CRM");
  // rule.tableColumns (M2-F1): which columns the table shows; otherwise the first 5.
  const shownColumns = rule.tableColumns ? rule.tableColumns.map((k) => [k, rule.item[k]]).filter(([, r]) => r) : columns.slice(0, 5);

  if (rule.fixedRows) {
    // Fixed rows (e.g. the 7 weekdays): edit in place.
    if (!rows.length && rule.emptyText) return <div className={cssClasses.empty}>{rule.emptyText}</div>;
    return (
      <div className={cssClasses.tableCard}>
        <table className={cssClasses.table}>
          <thead><tr>{Object.entries(rule.item).map(([k, r]) => <th key={k}>{r.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {Object.entries(rule.item).map(([k, r]) => (
                  <td key={k}>
                    {r.readOnly ? cellText(r, row[k], options) : (
                      <FieldInput compact rule={r} value={row[k]} options={options} disabled={disabled} error={rowErrors[i]?.[k]}
                        onChange={(v) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)))} />
                    )}
                    {rowErrors[i]?.[k] && <p className={cssClasses.error}>{rowErrors[i][k]}</p>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={cssClasses.listWrap}>
      <div className={cssClasses.listHead}>
        <span className={cssClasses.label}>{rows.length} {rows.length === 1 ? "row" : "rows"}</span>
        {rule.canAdd && !disabled && (
          <Button style={btnPrimary} btnName={rule.addLabel || "+ Add"} clicked={() => setEditing({ index: -1, row: newRow(rule.item, rows.length) })} />
        )}
      </div>
      {rows.length === 0 ? (
        <div className={cssClasses.empty}>{rule.emptyText || "Nothing added yet."}</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead>
              <tr>
                {shownColumns.map(([k, r]) => <th key={k}>{r.label}</th>)}
                {hasActive && <th>Active</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={rowErrors[i] ? cssClasses.rowError : ""}>
                  {shownColumns.map(([k, r]) => <td key={k}>{cellText(r, row[k], options)}</td>)}
                  {hasActive && (
                    <td style={{ width: "1%" }}>
                      <Switch size="small" checked={!!row.isActive} disabled={disabled} inputProps={{ "aria-label": "Active" }}
                        onChange={(e) => {
                          const turnOn = e.target.checked;
                          const apply = () => onChange(rows.map((x, j) => (j === i ? { ...x, isActive: turnOn } : x)));
                          if (turnOn) apply();
                          else setConfirm({ apply, name: cellText(columns[0][1], row[columns[0][0]], options), id: idKey ? row[idKey] : null });
                        }} />
                    </td>
                  )}
                  <td style={{ width: "1%", whiteSpace: "nowrap", textAlign: "right" }}>
                    {!disabled && (
                      <span className={cssClasses.rowActions}>
                        {rule.reorder && (
                          <>
                            <button type="button" className={cssClasses.pillEdit} aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                            <button type="button" className={cssClasses.pillEdit} aria-label="Move down" disabled={i === rows.length - 1} onClick={() => move(i, 1)}>↓</button>
                          </>
                        )}
                        <button type="button" className={cssClasses.pillEdit} onClick={() => setEditing({ index: i, row })}>Edit</button>
                        {rule.canDelete && (
                          <button type="button" className={cssClasses.pillEdit}
                            onClick={() => (rule.confirmDelete ? setToDelete(i) : onChange(rows.filter((_, j) => j !== i)))}>Remove</button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {Object.entries(rowErrors).map(([i, errs]) => (
        <p key={i} className={cssClasses.error}>
          Row {Number(i) + 1}: {Object.entries(errs).map(([k, m]) => `${rule.item[k]?.label || k}: ${errorText(m)}`).join(" · ")}
        </p>
      ))}
      {rule.footerNote && (
        <div className={cssClasses.tableCard} style={{ padding: "1.2rem 1.6rem", fontSize: "1.4rem", color: "#344054", marginTop: "1.2rem" }}>
          {rule.footerNote}
        </div>
      )}
      <RowModal open={!!editing} isNew={editing?.index === -1} item={rule.item} row={editing?.row} options={options} preview={rule.preview}
        title={`${editing?.index === -1 ? "Add" : "Edit"} ${singular}`}
        onCancel={() => setEditing(null)}
        onSave={(row) => {
          onChange(editing.index === -1 ? [...rows, row] : rows.map((x, j) => (j === editing.index ? row : x)));
          setEditing(null);
        }} />
      <ConfirmDialog open={!!confirm} title={`Switch off ${confirm?.name}?`}
        text={rule.offConfirmText
          ? rule.offConfirmText.replace("{name}", confirm?.name || "").replace("{projects}", usedBy(confirm?.id).join(", ") || "none")
          : "It will not be offered for anything new. Records that already use it keep it."}
        onCancel={() => setConfirm(null)} onConfirm={() => { confirm.apply(); setConfirm(null); }} />
      <ConfirmDialog open={toDelete !== null} title={`Delete this ${singular}?`}
        text="It is removed from the list when you save. Bookings already assigned keep their CRM."
        onCancel={() => setToDelete(null)}
        onConfirm={() => { onChange(rows.filter((_, j) => j !== toDelete)); setToDelete(null); }} />
    </div>
  );
}

// ---- one section's form ---------------------------------------------------------------
// custom: { fieldKey: (props) => element } draws that field with a section's own editor
// (Notifications › events); it gets the same props as Field.
function SectionForm({ def, mode, values, onChange, disabled, options, errors, custom }) {
  const shown = new Set(fieldsFor(def, mode).map(([k]) => k));
  return (
    <div className={cssClasses.col}>
      {def.groups.map(([title, keys]) => {
        const visible = keys.filter((k) => shown.has(k));
        if (!visible.length) return null;
        return (
          <div key={title} className={cssClasses.card}>
            {def.groups.length > 1 && <p className={cssClasses.cardTitle}>{title}</p>}
            <div className={cssClasses.fieldRow}>
              {visible.map((k) => {
                const props = { rule: def.fields[k], value: values[k], options, disabled, error: errors[k], onChange: (v) => onChange({ ...values, [k]: v }) };
                return custom?.[k] ? <React.Fragment key={k}>{custom[k](props)}</React.Fragment> : <Field key={k} {...props} />;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SectionNav({ items, current, onSelect, legend }) {
  return (
    <nav className={cssClasses.nav} aria-label="Sections">
      {items.map((item, i) => (
        <button key={item.key} type="button" className={`${cssClasses.navItem} ${item.key === current ? cssClasses.navItemOn : ""}`}
          aria-current={item.key === current ? "page" : undefined} onClick={() => onSelect(item.key)}>
          <span className={cssClasses.navNum}>{i + 1}</span>
          <span>{item.label}</span>
          {item.badge && <span className={`${cssClasses.navBadge} ${item.badge === "C" ? cssClasses.navBadgeChanged : ""}`}>{item.badge}</span>}
        </button>
      ))}
      {legend && <p className={`${cssClasses.hint} ${cssClasses.navLegend}`}>{legend}</p>}
    </nav>
  );
}

function SaveBar({ dirty, saving, onCancel, onSave }) {
  return (
    <div className={cssClasses.saveBar}>
      <Button style={btnSecondary} btnName="Cancel" disable={!dirty || saving} clicked={onCancel} />
      <Button style={btnPrimary} btnName="Save" disable={!dirty || saving} isLoading={saving} clicked={onSave} />
    </div>
  );
}

function useMeta(projectId) {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const reload = () =>
    getSettingsMeta(projectId)
      .then(setMeta)
      .catch((err) => setError(settingsError(err).message));
  useEffect(() => {
    reload();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { meta, error, reload };
}

// The pages around these screens grow with their content, so "height: 100%" is
// not enough: fit the screen to the space left in the window, so the header and
// the section menu stay put and only the form scrolls.
function useFitToWindow() {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    const fit = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(window.innerWidth > 900 ? Math.max(320, window.innerHeight - top - 16) : null);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  return [ref, height];
}

function Shell({ title, chip, sub, aside, nav, children }) {
  const [ref, height] = useFitToWindow();
  return (
    <div ref={ref} className={cssClasses.wrapper} style={height ? { height } : undefined}>
      <div className={cssClasses.head}>
        <div>
          <div className={cssClasses.headTitleRow}>
            <p className={cssClasses.headTitle}>{title}</p>
            {chip && <span className={cssClasses.chipOk}>{chip}</span>}
          </div>
          <p className={cssClasses.headSub}>{sub}</p>
        </div>
        {aside}
      </div>
      <div className={cssClasses.grid}>
        {nav}
        <div className={cssClasses.main}>{children}</div>
      </div>
    </div>
  );
}

const sectionHead = (label, sub) => (
  <div>
    <p className={cssClasses.sectionTitle}>{label}</p>
    {sub && <p className={cssClasses.headSub}>{sub}</p>}
  </div>
);

// ---- Settings › CRM 2.0 ----------------------------------------------------------------------
export function Crm2CompanySettings() {
  const canEditSettings = useCanEdit(["Settings", "CRM 2.0", "Edit"]);
  const canEditSignatories = useCanEdit(SIG_EDIT);
  const canViewSignatories = useCanEdit(SIG_VIEW) || canEditSignatories;
  const canEditNotifications = useCanEdit(NOTE_EDIT);
  const canViewNotifications = useCanEdit(NOTE_VIEW) || canEditNotifications;
  const canGoLiveNotifications = useCanEdit(NOTE_GO_LIVE);
  const { meta, error: metaError, reload: reloadMeta } = useMeta(null);
  const [section, setSection] = useState(COMPANY_NAV[0]);
  const [row, setRow] = useState(null);
  const [counts, setCounts] = useState(null);
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [consentTab, setConsentTab] = useState(CONSENT_TABS[0]);
  const canEdit = section === SIGNATORIES ? canEditSignatories : section === NOTIFICATIONS ? canEditNotifications : canEditSettings;

  const def = meta?.sections?.[section];
  const keys = useMemo(() => (def ? editableKeys(def, "company") : []), [def]);

  useEffect(() => {
    if (!meta || section === OFFICES || section === RIDERS) return;
    setLoading(true);
    setLoadError("");
    getCompanySettings(section)
      .then((data) => {
        setRow(data.company);
        setCounts({ on: data.projectsOnCrm2, off: data.projectsOnTodaysFlow });
        setDraft(data.company.values);
        setErrors({});
      })
      .catch((err) => setLoadError(settingsError(err).message))
      .finally(() => setLoading(false));
  }, [section, meta]);

  const dirty = !!row && !same(pickKeys(draft, keys), pickKeys(row.values, keys));

  function save() {
    setSaving(true);
    saveCompanySettings(section, pickKeys(draft, keys), row.last_updated_at)
      .then((data) => {
        setRow(data.company);
        setDraft(data.company.values);
        setErrors({});
        successTost(`${def.label} saved`);
        reloadMeta(); // pick lists (lenders, holiday lists, districts) may have changed
      })
      .catch((err) => {
        const { message, fields } = settingsError(err);
        setErrors(fields);
        errorTost(message);
      })
      .finally(() => setSaving(false));
  }

  function select(next) {
    if (dirty && !window.confirm("You have unsaved changes. Leave this section without saving?")) return;
    setRow(null);
    setSection(next);
  }

  if (metaError) return <div className={cssClasses.wrapper}><div className={cssClasses.empty}>{metaError}</div></div>;
  if (!meta) return <div className={cssClasses.wrapper}><div className={cssClasses.empty}>Loading…</div></div>;

  const navItems = COMPANY_NAV
    .filter((k) => (k !== SIGNATORIES || canViewSignatories) && (k !== NOTIFICATIONS || canViewNotifications))
    .filter((k) => SPECIAL[k] || meta.sections[k]) // a section the server does not send yet is left out
    .map((k) => ({ key: k, label: SPECIAL[k]?.label || meta.sections[k].label }));
  const current = SPECIAL[section] || def;
  // Notifications: the events list has its own editor (NotificationsSection.jsx); the rest is the framework's
  const custom = section === NOTIFICATIONS ? { events: (p) => <NotificationEvents {...p} canGoLive={canGoLiveNotifications} /> } : undefined;

  return (
    <Shell
      title="CRM 2.0 company defaults"
      sub="Every project on CRM 2.0 uses these settings unless its own CRM 2.0 tab changes them."
      aside={counts && (
        <div className={cssClasses.counts}>
          <p className={cssClasses.hint} style={{ margin: 0 }}>Projects</p>
          <b>{counts.on}</b> on CRM 2.0 · <b>{counts.off}</b> on today's flow
        </div>
      )}
      nav={<SectionNav items={navItems} current={section} onSelect={select} />}
    >
      {sectionHead(current.label, current.sub)}
      {/* M1-F6: Customer Consent has tabs; "Consent points" is this section's own form. */}
      {section === "CUSTOMER_CONSENT" && (
        <div style={{ display: "flex", gap: "0.8rem", borderBottom: "1px solid #EAECF0" }}>
          {CONSENT_TABS.map((t) => (
            <button key={t} type="button" onClick={() => setConsentTab(t)}
              style={{ border: 0, background: "none", padding: "0.8rem 0.4rem", fontSize: "1.4rem", cursor: "pointer", color: t === consentTab ? "#155EEF" : "#667085", borderBottom: t === consentTab ? "2px solid #155EEF" : "2px solid transparent" }}>
              {t}
            </button>
          ))}
        </div>
      )}
      {section === "CUSTOMER_CONSENT" && consentTab !== "Consent points" ? (
        consentTab === "Documents" ? <Crm2ConsentDocuments />
          : consentTab === "Terms" ? <Crm2ConsentTerms />
          : consentTab === "Ask now" ? <Crm2ConsentAskNow />
          : <Crm2ConsentReport />
      ) : section === OFFICES ? (
        <OfficesSection meta={meta} canEdit={canEdit} onSaved={reloadMeta} />
      ) : section === RIDERS ? (
        <RidersSection meta={meta} canEdit={canEdit} onSaved={reloadMeta} />
      ) : loading ? (
        <div className={cssClasses.empty}>Loading…</div>
      ) : loadError ? (
        <div className={cssClasses.empty}>{loadError}</div>
      ) : row && (
        <>
          <SectionForm def={def} mode="company" values={draft} onChange={setDraft} disabled={!canEdit || saving} options={meta.options} errors={errors} custom={custom} />
          {canEdit && <SaveBar dirty={dirty} saving={saving} onSave={save} onCancel={() => { setDraft(row.values); setErrors({}); }} />}
        </>
      )}
    </Shell>
  );
}

// ---- Banking › Lenders (M10-F1) ------------------------------------------------------------
// The same CF lender list as Settings › CRM 2.0 › CF lenders (section CF_LENDERS),
// opened from the Banking menu. The server allows it with Banking › Lenders View / Edit.
export function Crm2LendersSection({ canEdit }) {
  const { meta, error: metaError } = useMeta(null);
  const [row, setRow] = useState(null);
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const def = meta?.sections?.CF_LENDERS;
  const keys = useMemo(() => (def ? editableKeys(def, "company") : []), [def]);

  useEffect(() => {
    if (!def) return;
    getCompanySettings("CF_LENDERS")
      .then((data) => {
        setRow(data.company);
        setDraft(data.company.values);
      })
      .catch((err) => setLoadError(settingsError(err).message));
  }, [def]);

  const dirty = !!row && !same(pickKeys(draft, keys), pickKeys(row.values, keys));

  function save() {
    setSaving(true);
    saveCompanySettings("CF_LENDERS", pickKeys(draft, keys), row.last_updated_at)
      .then((data) => {
        setRow(data.company);
        setDraft(data.company.values);
        setErrors({});
        successTost("Lenders saved");
      })
      .catch((err) => {
        const { message, fields } = settingsError(err);
        setErrors(fields);
        errorTost(message);
      })
      .finally(() => setSaving(false));
  }

  if (metaError || loadError) return <div className={cssClasses.empty}>{metaError || loadError}</div>;
  if (!def || !row) return <div className={cssClasses.empty}>Loading…</div>;
  return (
    <div className={cssClasses.col}>
      {sectionHead(def.label, def.sub)}
      <SectionForm def={def} mode="company" values={draft} onChange={setDraft} disabled={!canEdit || saving} options={meta.options} errors={errors} />
      {canEdit && <SaveBar dirty={dirty} saving={saving} onSave={save} onCancel={() => { setDraft(row.values); setErrors({}); }} />}
    </div>
  );
}

// Sub-Registrar offices: own table, one row per office.
function OfficesSection({ meta, canEdit, onSaved }) {
  const [offices, setOffices] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // { office, values }
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const fields = meta.officeFields;

  const load = () => getOffices().then(setOffices).catch((err) => setError(settingsError(err).message));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function openNew() {
    try {
      const { company } = await getCompanySettings("APPOINTMENT");
      const values = {};
      Object.entries(fields).forEach(([k, r]) => (values[k] = emptyValue(r, k)));
      values.openDays = company.values.newOfficeDefaultDays || [];
      setFieldErrors({});
      setEditing({ office: null, values });
    } catch (err) {
      errorTost(settingsError(err).message);
    }
  }

  function persist(office, values) {
    setSaving(true);
    const call = office ? saveOffice(office.crm2SubRegistrarOfficeId, values, office.last_updated_at) : addOffice(values);
    return call
      .then(() => {
        successTost(office ? "Office saved" : "Office added");
        setEditing(null);
        load();
        onSaved();
      })
      .catch((err) => {
        const { message, fields: f } = settingsError(err);
        setFieldErrors(f);
        errorTost(message);
      })
      .finally(() => setSaving(false));
  }

  const daysText = (o) => {
    const names = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return (o.openDays || []).filter((d) => d.isOpen).map((d) => names[d.weekday]).join(", ") || "Closed";
  };

  if (error) return <div className={cssClasses.empty}>{error}</div>;
  if (!offices) return <div className={cssClasses.empty}>Loading…</div>;

  return (
    <div className={cssClasses.listWrap}>
      <div className={cssClasses.listHead}>
        <span className={cssClasses.label}>{offices.length} Sub-Registrar {offices.length === 1 ? "office" : "offices"}</span>
        {canEdit && <Button style={btnPrimary} btnName="+ Add Sub-Registrar office" clicked={openNew} />}
      </div>
      {offices.length === 0 ? (
        <div className={cssClasses.empty}>No Sub-Registrar offices yet.</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead><tr><th>Office</th><th>Days open</th><th>Projects using it</th><th>Active</th><th /></tr></thead>
            <tbody>
              {offices.map((o) => (
                <tr key={o.crm2SubRegistrarOfficeId}>
                  <td><b>{o.officeName}</b><br /><span className={cssClasses.hint}>{o.area} · {o.igrOfficeName}</span></td>
                  <td>{daysText(o)}</td>
                  <td>{o.projectCount ? o.projectCount : <span className={cssClasses.chipMuted}>No projects yet</span>}</td>
                  <td style={{ width: "1%" }}>
                    <Switch size="small" checked={!!o.isActive} disabled={!canEdit || saving} inputProps={{ "aria-label": "Active" }}
                      onChange={(e) => {
                        const isActive = e.target.checked;
                        if (isActive) persist(o, { isActive });
                        else setConfirm(o);
                      }} />
                  </td>
                  <td style={{ width: "1%", textAlign: "right" }}>
                    {canEdit && (
                      <button type="button" className={cssClasses.pillEdit} onClick={() => { setFieldErrors({}); setEditing({ office: o, values: pickKeys(o, Object.keys(fields)) }); }}>Edit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Modal open onClose={(_, reason) => reason !== "backdropClick" && setEditing(null)} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
          <Box className={`${cssClasses.modalWrapper} ${cssClasses.modalWide}`}>
            <div className={cssClasses.modalHeader}>
              <span>{editing.office ? "Edit Sub-Registrar office" : "Add Sub-Registrar office"}</span>
              <span className={cssClasses.modalClose} onClick={() => setEditing(null)} tabIndex={0} role="button" aria-label="Close"><Close width="1.2rem" height="1.2rem" /></span>
            </div>
            <div className={cssClasses.modalContent}>
              {Object.entries(fields).map(([k, r]) => (
                <Field key={k} rule={r} value={editing.values[k]} options={meta.options} error={fieldErrors[k]} disabled={saving}
                  onChange={(v) => setEditing({ ...editing, values: { ...editing.values, [k]: v } })} />
              ))}
            </div>
            <div className={cssClasses.modalFooter}>
              <Button style={btnSecondary} btnName="Cancel" clicked={() => setEditing(null)} />
              <Button style={btnPrimary} btnName={editing.office ? "Save" : "Add"} isLoading={saving} disable={saving} clicked={() => persist(editing.office, editing.values)} />
            </div>
          </Box>
        </Modal>
      )}
      <ConfirmDialog open={!!confirm} title={`Make ${confirm?.officeName} inactive?`}
        text="Booked appointments stay as they are. No new appointments are offered at this office; projects that use only this office fail the health check."
        onCancel={() => setConfirm(null)} onConfirm={() => { persist(confirm, { isActive: false }); setConfirm(null); }} />
    </div>
  );
}

// Project › CRM 2.0 › Authorised signatory: what each pick prints (read only).
function SignatoryPreview({ values, options, emptyText }) {
  const byId = new Map((options.signatories || []).map((s) => [s.value, s]));
  const landowner = new Map((options.landownerProjects || []).map((p) => [p.value, p.label]));
  const picks = [["VJ units", values.signatoryId], ...(values.landowners || []).map((l) => [landowner.get(l.landownerProjectId) || "Landowner project", l.signatoryId])];
  return (
    <div className={cssClasses.card}>
      <p className={cssClasses.cardTitle}>What prints on the allotment letter</p>
      {!values.signatoryId && <p className={cssClasses.error}>{emptyText}</p>}
      {picks.map(([label, id], i) => {
        const s = id ? byId.get(id) : null;
        return (
          <div key={`${label}-${i}`} style={{ display: "flex", flexDirection: "column", gap: ".6rem", padding: ".8rem 0", borderTop: i ? "1px solid #EAECF0" : "none" }}>
            <span className={cssClasses.label}>{label}</span>
            {!s ? (
              <span className={cssClasses.hint}>No signatory picked</span>
            ) : s.isActive === false ? (
              <span className={cssClasses.error}>{s.label} is switched off: letters stop until another signatory is picked</span>
            ) : (
              <div style={{ display: "flex", gap: "1.2rem", alignItems: "center", flexWrap: "wrap" }}>
                <span className={cssClasses.hint}>{s.label}</span>
                <ImagePreview path={s.signatureImage} alt="Signature" height="4.8rem" />
                <ImagePreview path={s.stampImage} alt="Stamp" height="4.8rem" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Rider pool (M15-F2, DC-141): Riders are users with the Rider role; one row per Rider.
function RidersSection({ meta, canEdit, onSaved }) {
  const [riders, setRiders] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // { rider, values }
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const offices = meta.options.offices || [];

  const load = () => getRiders().then(setRiders).catch((err) => setError(settingsError(err).message));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function open(rider) {
    setFieldErrors({});
    setEditing({
      rider,
      values: rider ? { name: rider.name, mobile: rider.mobile, officeIds: rider.officeIds, isActive: rider.isActive } : { name: "", mobile: "", officeIds: [], isActive: true },
    });
  }

  function persist(rider, values) {
    setSaving(true);
    const call = rider ? saveRider(rider.userId, values, rider.last_updated_at) : addRider(values);
    return call
      .then(() => {
        successTost(rider ? "Rider saved" : "Rider added");
        setEditing(null);
        load();
        onSaved(); // the Riders pick list in Appointments › Rider choice
      })
      .catch((err) => {
        const { message, fields } = settingsError(err);
        setFieldErrors(fields);
        errorTost(message);
      })
      .finally(() => setSaving(false));
  }

  // an office switched off stays ticked on a Rider who has it, but is not offered for new picks
  const officeChoices = (current) => offices.filter((o) => o.isActive || current.includes(o.value));
  const set = (k, v) => setEditing({ ...editing, values: { ...editing.values, [k]: v } });

  if (error) return <div className={cssClasses.empty}>{error}</div>;
  if (!riders) return <div className={cssClasses.empty}>Loading…</div>;

  return (
    <div className={cssClasses.listWrap}>
      <div className={cssClasses.listHead}>
        <span className={cssClasses.label}>{riders.length} {riders.length === 1 ? "Rider" : "Riders"}</span>
        {canEdit && <Button style={btnPrimary} btnName="+ Add Rider" clicked={() => open(null)} />}
      </div>
      {riders.length === 0 ? (
        <div className={cssClasses.empty}>No Riders yet.</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead><tr><th>Rider</th><th>Sub-Registrar offices</th><th>Open registrations</th><th>Active</th><th /></tr></thead>
            <tbody>
              {riders.map((r) => (
                <tr key={r.userId}>
                  <td><b>{r.name}</b><br /><span className={cssClasses.hint}>{r.mobile}</span></td>
                  <td>
                    {r.offices.length ? r.offices.map((o) => (
                      <span key={o.officeId} style={{ display: "block" }}>{o.officeName}{!o.isActive && <span className={cssClasses.chipMuted}> inactive</span>}</span>
                    )) : <span className={cssClasses.chipMuted}>No office</span>}
                  </td>
                  <td>{r.openRegistrations || <span className={cssClasses.hint}>None</span>}</td>
                  <td style={{ width: "1%" }}>
                    <Switch size="small" checked={!!r.isActive} disabled={!canEdit || saving} inputProps={{ "aria-label": "Active" }}
                      onChange={(e) => {
                        if (e.target.checked) persist(r, { isActive: true });
                        else setConfirm(r);
                      }} />
                  </td>
                  <td style={{ width: "1%", textAlign: "right" }}>
                    {canEdit && <button type="button" className={cssClasses.pillEdit} onClick={() => open(r)}>Edit</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Modal open onClose={(_, reason) => reason !== "backdropClick" && setEditing(null)} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
          <Box className={cssClasses.modalWrapper}>
            <div className={cssClasses.modalHeader}>
              <span>{editing.rider ? "Edit Rider" : "Add Rider"}</span>
              <span className={cssClasses.modalClose} onClick={() => setEditing(null)} tabIndex={0} role="button" aria-label="Close"><Close width="1.2rem" height="1.2rem" /></span>
            </div>
            <div className={cssClasses.modalContent}>
              <TextField label="Name" required size="small" fullWidth value={editing.values.name} disabled={saving}
                error={!!fieldErrors.name} helperText={fieldErrors.name} inputProps={{ maxLength: 80, style: inputStyle }} InputLabelProps={{ style: inputStyle }}
                onChange={(e) => set("name", e.target.value)} />
              <TextField label="Mobile" required size="small" fullWidth value={editing.values.mobile} disabled={saving}
                error={!!fieldErrors.mobile} helperText={fieldErrors.mobile || "The Rider signs in with this mobile and an OTP"}
                inputProps={{ inputMode: "numeric", maxLength: 14, style: inputStyle }} InputLabelProps={{ style: inputStyle }}
                onChange={(e) => set("mobile", e.target.value)} />
              <div>
                <p className={cssClasses.label} style={{ margin: "0 0 .6rem" }}>Sub-Registrar offices *</p>
                <div className={cssClasses.checkWrap}>
                  {officeChoices(editing.values.officeIds).map((o) => (
                    <FormControlLabel key={o.value} disabled={saving}
                      control={<Checkbox size="small" checked={editing.values.officeIds.includes(o.value)}
                        onChange={(e) => set("officeIds", e.target.checked ? [...editing.values.officeIds, o.value] : editing.values.officeIds.filter((v) => v !== o.value))} />}
                      label={<span className={cssClasses.radioText}>{o.label}{!o.isActive ? " (inactive)" : ""}</span>} />
                  ))}
                  {!officeChoices(editing.values.officeIds).length && <p className={cssClasses.hint}>Add a Sub-Registrar office first.</p>}
                </div>
                {fieldErrors.officeIds && <p className={cssClasses.error}>{fieldErrors.officeIds}</p>}
              </div>
            </div>
            <div className={cssClasses.modalFooter}>
              <Button style={btnSecondary} btnName="Cancel" clicked={() => setEditing(null)} />
              <Button style={btnPrimary} btnName={editing.rider ? "Save" : "Add"} isLoading={saving} disable={saving} clicked={() => persist(editing.rider, editing.values)} />
            </div>
          </Box>
        </Modal>
      )}
      <ConfirmDialog open={!!confirm} title={`Make ${confirm?.name} inactive?`}
        text={`Registrations already with ${confirm?.name} stay with them until the Document Manager changes the Rider. No new registrations are offered to them, and they can no longer sign in.`}
        onCancel={() => setConfirm(null)} onConfirm={() => { persist(confirm, { isActive: false }); setConfirm(null); }} />
    </div>
  );
}

// ---- Project › CRM 2.0 ----------------------------------------------------------------------
export function Crm2ProjectSettings({ projectId }) {
  const canEditProject = useCanEdit(["Project", "View Project Details", "CRM 2.0", "Edit"]);
  const canEditSignatories = useCanEdit(SIG_EDIT);
  const { meta, error: metaError, reload: reloadMeta } = useMeta(projectId);
  const [section, setSection] = useState(PROJECT_NAV[0]);
  const [data, setData] = useState(null);
  const [on, setOn] = useState(false);
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  // the project's signatory pick is changed only with the signatories Edit permission
  const canEdit = section === "SIGNATORY" ? canEditSignatories : canEditProject;

  const def = meta?.sections?.[section];
  const projectOnly = def?.rows === "PROJECT";
  const keys = useMemo(() => (def ? editableKeys(def, "project") : []), [def]);
  const own = data?.projectSettings || null;
  const savedOn = projectOnly ? !!own : !!own?.useProjectValues;
  const base = useMemo(() => {
    if (!def) return {};
    const defaults = {};
    fieldsFor(def, "project").forEach(([k, r]) => (defaults[k] = emptyValue(r, k)));
    // projectDefaults: rows the server builds before the first save (SIGNATORY: one per landowner project)
    return { ...defaults, ...pickKeys(data?.company?.values, keys), ...pickKeys(data?.projectDefaults, keys) };
  }, [def, data, keys]);
  const ownValues = { ...base, ...pickKeys(own?.values, keys) };

  function apply(next) {
    setData(next);
    setErrors({});
  }

  useEffect(() => {
    if (!meta || section === TEAM) return;
    setLoading(true);
    setLoadError("");
    getProjectSettings(section, projectId)
      .then(apply)
      .catch((err) => setLoadError(settingsError(err).message))
      .finally(() => setLoading(false));
  }, [section, projectId, meta]);

  // Draft follows what is saved whenever the section or its row changes.
  useEffect(() => {
    if (!def || !data) return;
    const isOn = def.rows === "PROJECT" || !!data.projectSettings?.useProjectValues;
    setOn(isOn);
    setDraft(isOn ? { ...base, ...pickKeys(data.projectSettings?.values, keys) } : base);
  }, [data, def]); // eslint-disable-line react-hooks/exhaustive-deps

  function save(useProjectValues, values) {
    setSaving(true);
    return saveProjectSettings(section, projectId, useProjectValues, values, own?.last_updated_at ?? null)
      .then((res) => {
        apply({ ...data, projectSettings: res.projectSettings });
        return true;
      })
      .catch((err) => {
        const { message, fields } = settingsError(err);
        setErrors(fields);
        errorTost(message);
        return false;
      })
      .finally(() => setSaving(false));
  }

  function toggle(checked) {
    if (checked) {
      setOn(true);
      setDraft(ownValues);
      successTost("Now set for this project: edit below");
    } else if (savedOn) {
      save(false, {}).then((ok) => ok && successTost("Back to the company default"));
    } else {
      setOn(false);
      setDraft(base);
      setErrors({});
    }
  }

  // Project-only sections: changed = differs from what is saved (or from empty when nothing is).
  // Sections with a company row: switching on is itself a change until it is saved.
  const dirty = on && (projectOnly
    ? !same(pickKeys(draft, keys), pickKeys(savedOn ? ownValues : base, keys))
    : !savedOn || !same(pickKeys(draft, keys), pickKeys(ownValues, keys)));

  function select(next) {
    if (dirty && !window.confirm("You have unsaved changes. Leave this section without saving?")) return;
    setData(null);
    setSection(next);
  }

  if (metaError) return <div className={cssClasses.wrapper}><div className={cssClasses.empty}>{metaError}</div></div>;
  if (!meta) return <div className={cssClasses.wrapper}><div className={cssClasses.empty}>Loading…</div></div>;

  const project = data?.project;
  const navItems = PROJECT_NAV.map((k) => ({
    key: k,
    label: SPECIAL[k]?.label || meta.sections[k].label,
    badge: k === section && data && meta.sections[k]?.rows === "BOTH" ? (savedOn ? "C" : "D") : undefined,
  }));
  const current = SPECIAL[section] || def;

  return (
    <Shell
      title={`CRM 2.0 for ${project?.projectName || ""}`}
      chip={project?.crm2SwitchedAt ? `On since ${dayjs(Number(project.crm2SwitchedAt)).format("DD MMM YYYY")}` : null}
      sub="Everything not changed here uses the company defaults (Settings › CRM 2.0)."
      nav={<SectionNav items={navItems} current={section} onSelect={select} legend="D = company default · C = changed for this project" />}
    >
      {sectionHead(current.label, section === TEAM ? current.sub : projectOnly ? def.sub : data ? (savedOn ? "This project uses its own values." : "This project uses the company defaults.") : "")}
      {section === TEAM ? (
        <TeamSection projectId={projectId} canEdit={canEdit} onSaved={reloadMeta} />
      ) : loading ? (
        <div className={cssClasses.empty}>Loading…</div>
      ) : loadError ? (
        <div className={cssClasses.empty}>{loadError}</div>
      ) : data && (
        <>
          {!projectOnly && (
            <div className={cssClasses.overrideBox}>
              <div>
                <span className={cssClasses.label}>{def.switchLabel}</span>
                <p className={cssClasses.hint}>
                  {on
                    ? "On: the values below apply to this project only. Turn off to go back to the company default."
                    : "Off: this project uses the company default. Turn on to edit the values below for this project."}
                </p>
              </div>
              <Switch checked={on} disabled={!canEdit || saving} onChange={(e) => toggle(e.target.checked)} inputProps={{ "aria-label": def.switchLabel }} />
            </div>
          )}
          <div className={on ? "" : cssClasses.locked} aria-disabled={!on}>
            <SectionForm def={def} mode="project" values={draft} onChange={setDraft} disabled={!on || !canEdit || saving} options={meta.options} errors={errors} />
          </div>
          {section === "SIGNATORY" && <SignatoryPreview values={draft} options={meta.options} emptyText={def.emptyText} />}
          {on && canEdit && (
            <SaveBar dirty={dirty} saving={saving}
              onCancel={() => {
                setErrors({});
                if (savedOn || projectOnly) setDraft(ownValues);
                else {
                  setOn(false);
                  setDraft(base);
                }
              }}
              onSave={() => save(true, pickKeys(draft, keys)).then((ok) => ok && successTost(`${def.label} saved for this project`))} />
          )}
        </>
      )}
    </Shell>
  );
}

// Team: tick the CRMs who get new bookings (Crm2ProjectCrms).
function TeamSection({ projectId, canEdit, onSaved }) {
  const [crms, setCrms] = useState(null);
  const [ticked, setTicked] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    getTeam(projectId)
      .then((d) => {
        setCrms(d.crms);
        setTicked(d.crms.filter((c) => c.ticked).map((c) => c.userId));
      })
      .catch((err) => setError(settingsError(err).message));
  useEffect(() => {
    load();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div className={cssClasses.empty}>{error}</div>;
  if (!crms) return <div className={cssClasses.empty}>Loading…</div>;
  const saved = crms.filter((c) => c.ticked).map((c) => c.userId);
  const dirty = !same([...ticked].sort(), [...saved].sort());

  function save() {
    setSaving(true);
    saveTeam(projectId, ticked)
      .then(() => {
        successTost("Team saved");
        load();
        onSaved();
      })
      .catch((err) => errorTost(settingsError(err).message))
      .finally(() => setSaving(false));
  }

  return (
    <div className={cssClasses.listWrap}>
      <div className={cssClasses.listHead}>
        <span className={cssClasses.label}>{ticked.length} of {crms.length} CRMs get new bookings</span>
      </div>
      {crms.length === 0 ? (
        <div className={cssClasses.empty}>No CRM has access to this project yet (Users › Assign Project).</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead><tr><th>CRM</th><th>Email</th><th>Gets new bookings</th></tr></thead>
            <tbody>
              {crms.map((c) => (
                <tr key={c.userId}>
                  <td>{c.name}{!c.isActive && <span className={cssClasses.chipMuted}> inactive</span>}</td>
                  <td>{c.email}</td>
                  <td style={{ width: "1%" }}>
                    <Switch size="small" checked={ticked.includes(c.userId)} disabled={!canEdit || saving || (!c.isActive && !ticked.includes(c.userId))}
                      inputProps={{ "aria-label": `Gets new bookings: ${c.name}` }}
                      onChange={(e) => setTicked(e.target.checked ? [...ticked, c.userId] : ticked.filter((u) => u !== c.userId))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && <SaveBar dirty={dirty} saving={saving} onSave={save} onCancel={() => setTicked(saved)} />}
    </div>
  );
}
